import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import { useProfile } from '../hooks/use-profile';
import { useUpdateEmail } from '../hooks/use-update-email';
import { changeEmailSchema, type ChangeEmailFormData } from '@/lib/validations/settings.schema';
import { CheckCircle2, Eye, EyeOff } from 'lucide-react';

export function EmailChangeContainer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: profile } = useProfile();
  const { mutate: updateEmail, isPending, isSuccess, isError, error } = useUpdateEmail();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<ChangeEmailFormData>({
    resolver: zodResolver(changeEmailSchema),
    defaultValues: {
      password: '',
      newEmail: '',
      confirmEmail: '',
    },
  });

  const handleSubmit = (data: ChangeEmailFormData) => {
    updateEmail({
      password: data.password,
      newEmail: data.newEmail,
    });
  };

  if (isSuccess) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center gap-4 text-center">
            <CheckCircle2 className="size-12 text-green-500" />
            <div>
              <h3 className="font-semibold text-lg">{t('settings.email.successTitle')}</h3>
              <p className="text-muted-foreground">{t('settings.email.successDescription')}</p>
            </div>
            <Button onClick={() => navigate('/settings/profile')}>
              {t('settings.email.backToProfile')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.email.title')}</CardTitle>
        <CardDescription>{t('settings.email.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          {isError && (
            <Alert variant="destructive">
              <AlertDescription>
                {(error as Error)?.message || t('settings.email.error')}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label>{t('settings.email.currentEmail')}</Label>
            <Input value={profile?.email ?? ''} disabled className="bg-muted" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="newEmail">{t('settings.email.newEmail')}</Label>
            <Input
              id="newEmail"
              type="email"
              autoComplete="email"
              {...form.register('newEmail')}
            />
            {form.formState.errors.newEmail && (
              <p className="text-sm text-destructive">
                {t(form.formState.errors.newEmail.message ?? '')}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmEmail">{t('settings.email.confirmEmail')}</Label>
            <Input
              id="confirmEmail"
              type="email"
              autoComplete="off"
              {...form.register('confirmEmail')}
            />
            {form.formState.errors.confirmEmail && (
              <p className="text-sm text-destructive">
                {t(form.formState.errors.confirmEmail.message ?? '')}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('settings.email.password')}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="pr-10"
                {...form.register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {form.formState.errors.password && (
              <p className="text-sm text-destructive">
                {t(form.formState.errors.password.message ?? '')}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              {t('settings.email.passwordHint')}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? t('settings.email.updating') : t('settings.email.update')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
